import { Color } from 'foundations-theming';
import { MarginType } from 'foundations-components/types';
import { ValueOf } from './types/Utils';

export const SOCIAL_MAP = {
  facebook: { logoName: 'facebook', showBackground: false },
  google: { logoName: 'google', showBackground: false },
  instagram: { logoName: 'instagram', showBackground: true },
  linkedin: { logoName: 'linkedin', showBackground: false },
  reddit: { logoName: 'reddit', showBackground: false },
  slack: { logoName: 'slack', showBackground: false },
  tiktok: { logoName: 'tiktok', showBackground: false },
  twitter: { logoName: 'x-social', showBackground: false },
  x: { logoName: 'x-social', showBackground: false },
  youtube: { logoName: 'youtube', showBackground: false },
  youtubeplay: { logoName: 'youtube', showBackground: false },
} as const;

export const AVATAR_SIZES = {
  xxs: 16,
  xs: 24,
  sm: 32,
  md: 48,
  lg: 72,
  xl: 108,
  responsive: 'responsive',
} as const;

export const AVATAR_LIST_SIZES_MARGIN_OFFSET: Record<
  keyof typeof AVATAR_SIZES,
  MarginType
> = {
  xxs: '-50',
  xs: '-100',
  sm: '-100',
  md: '-200',
  lg: '-200',
  xl: '-400',
  responsive: '0',
};

export const DEFAULT_AVATAR_TYPES = {
  AI_AGENT: 'AI_AGENT',
  CAMPAIGN: 'CAMPAIGN',
  CONTACT: 'CONTACT',
  COMPANIES: 'COMPANIES',
  PRODUCT: 'PRODUCT',
  TICKET: 'TICKET',
  DEAL: 'DEAL',
  MARKETING_EVENT: 'MARKETING_EVENT',
} as const;

export type DefaultAvatarType = ValueOf<typeof DEFAULT_AVATAR_TYPES>;

export const AVATAR_TYPES = {
  // AI Agents
  'ai-agent': DEFAULT_AVATAR_TYPES.AI_AGENT,

  // Campaigns
  campaign: DEFAULT_AVATAR_TYPES.CAMPAIGN,

  // Companies
  company: DEFAULT_AVATAR_TYPES.COMPANIES,
  companyId: DEFAULT_AVATAR_TYPES.COMPANIES,
  domain: DEFAULT_AVATAR_TYPES.COMPANIES,
  enrichmentDomain: DEFAULT_AVATAR_TYPES.COMPANIES,

  // Contacts
  contact: DEFAULT_AVATAR_TYPES.CONTACT,
  email: DEFAULT_AVATAR_TYPES.CONTACT,
  emails: DEFAULT_AVATAR_TYPES.CONTACT,
  enrichmentEmail: DEFAULT_AVATAR_TYPES.CONTACT,
  hubSpotUserEmail: DEFAULT_AVATAR_TYPES.CONTACT,
  hubSpotUserEmails: DEFAULT_AVATAR_TYPES.CONTACT,
  vid: DEFAULT_AVATAR_TYPES.CONTACT,

  // Deals
  deal: DEFAULT_AVATAR_TYPES.DEAL,

  // Products
  productId: DEFAULT_AVATAR_TYPES.PRODUCT,

  // Tickets
  ticket: DEFAULT_AVATAR_TYPES.TICKET,

  // Marketing Events
  marketingEventId: DEFAULT_AVATAR_TYPES.MARKETING_EVENT,
} as const;

export const AVATAR_TYPE_ICONS = {
  COMPANIES: 'companies',
  CONTACT: 'contacts',
  PRODUCT: 'product',
  TICKET: 'ticket',
  DEAL: 'deals',
  MARKETING_EVENT: 'marketing-event',
} as const;

export const RETINA_MULTIPLIER = 2;
export const API_KNOWN_KEY_MAPPINGS = {
  contact: 'knownContacts',
  company: 'knownCompanies',
};

export const API_KNOWN_VALUE_MAPPINGS = {
  contact: {
    primaryIdentifier: 'vid',
    secondaryIdentifier: 'email',
  },
  company: {
    primaryIdentifier: 'companyId',
    secondaryIdentifier: 'domain',
  },
};

export const API_IDENTIFIER_MAPPINGS = {
  // vids
  contact: 'vids',
  vids: 'vids',
  vid: 'vids',

  // companyIds
  company: 'companyIds',
  companyId: 'companyIds',

  // emails
  email: 'emails',
  enrichmentEmail: 'emails',

  // domains
  domain: 'domains',
  enrichmentDomain: 'domains',

  // hubSpotUserEmails
  'ai-agent': 'hubSpotUserEmails',
  hubSpotUserEmails: 'hubSpotUserEmails',
  hubSpotUserEmail: 'hubSpotUserEmails',

  // productIds
  product: 'productIds',
  productId: 'productIds',

  // marketingEventIds
  marketingEventId: 'marketingEventIds',
};

export const DEFAULT_MAX_UPLOAD_SIZE_MB = 4;

export const OBJECT_TYPE_TO_ENDPOINT_MAPPINGS = {
  contact: 'contacts/vid',
  vid: 'contacts/vid',
  company: 'companies/companyId',
  companyId: 'companies/companyId',
  product: 'products/productId',
  productId: 'products/productId',
  marketingEventId: 'marketing-events/marketingEventId',
};

export const OBJECT_TYPES_THAT_REQUIRE_PORTAL_ID = [
  'contact',
  'vid',
  'company',
  'companyId',
  'product',
  'productId',
  'marketingEventId',
];

export const AVATAR_SOURCE_TYPES = {
  DEFAULT_COMPANY_AVATAR: 'default-company-avatar',
  DEFAULT_CONTACT_AVATAR: 'default-contact-avatar',
  KNOWN_SRC: 'known-src',
};

export const MULTI_AVATAR_DEFAULT_BORDER_COLOR = 'border-core-on-fill-default';
export const MULTI_AVATAR_DEFAULT_BORDER_WIDTH = '200';
export const MULTI_AVATAR_DEFAULT_BORDER_STYLE = 'solid';

export const AVATAR_BACKGROUND_COLOR_THEME_KEY =
  'fill-accent-neutral-default' as const;
export const AVATAR_ICON_COLOR_THEME_KEY = 'icon-primary-default' as const;

export const TRELLIS_DEFAULT_COLORS = {
  iconColor: 'icon-core-default' as Color,
  backgroundColor: 'specialty-table-header-hover' as Color,
} as const;

export const DEFAULT_ICON_COLOR_MAP: Record<
  ValueOf<typeof DEFAULT_AVATAR_TYPES>,
  { iconColor: Color; backgroundColor: Color }
> = {
  [DEFAULT_AVATAR_TYPES.AI_AGENT]: {
    iconColor: 'breeze-icon-secondary-default' as Color,
    backgroundColor: 'fill-accent-magenta-subtle-alt',
  },
  [DEFAULT_AVATAR_TYPES.CAMPAIGN]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.CONTACT]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.COMPANIES]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.PRODUCT]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.TICKET]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.DEAL]: TRELLIS_DEFAULT_COLORS,
  [DEFAULT_AVATAR_TYPES.MARKETING_EVENT]: TRELLIS_DEFAULT_COLORS,
} as const;

export const LOOKUP_TYPE_TO_OBJECT_TYPE_ID: Record<string, string> = {
  contact: '0-1',
  vid: '0-1',
  company: '0-2',
  companyId: '0-2',
  productId: '0-7',
  marketingEventId: '0-54',
};

export const DEFAULT_PROPS = {
  showTooltip: true,
  size: 'sm',
  childMarginInlineStart: '300',
  childTruncateLength: 200,
} as const;
